import { IS_PROD, REDDIT_PIXEL_ID } from '@/lib/constant'
import type { ConversionEventData, ConversionEventType } from './types'
import { CONVERSION_EVENT_TYPES } from './types'
import { hashEmail, hashString, generateConversionId } from './utils'

/**
 * Reddit Conversions API
 * https://ads-api.reddit.com/docs/v2/#tag/Conversions-API
 */
export async function sendToRedditConversions(
  eventData: ConversionEventData,
): Promise<void> {
  if (!IS_PROD) {
    console.log('Sending Reddit Conversions API Test Event:', eventData)
  }

  try {
    const conversionToken = process.env.REDDIT_CONVERSION_API_TOKEN
    const pixelId = REDDIT_PIXEL_ID

    if (!conversionToken || !pixelId) {
      console.warn('Reddit Conversions API credentials not configured')
      return
    }

    // 映射事件类型到Reddit标准事件（仅使用Reddit支持的事件类型）
    // 不支持的事件将作为自定义事件发送
    const redditEventMap: Record<ConversionEventType, string | null> = {
      [CONVERSION_EVENT_TYPES.SIGNUP]: 'SignUp',
      [CONVERSION_EVENT_TYPES.REFERRAL_SIGNUP]: 'SignUp',
      [CONVERSION_EVENT_TYPES.LOGIN]: null, // 自定义事件: "Login"
      [CONVERSION_EVENT_TYPES.START_CODING]: null, // 自定义事件: "StartCoding"
      [CONVERSION_EVENT_TYPES.FINISH_CODING]: 'Lead',
      [CONVERSION_EVENT_TYPES.CHECKOUT]: 'AddToCart',
      [CONVERSION_EVENT_TYPES.ORDER_SUCCESS]: 'Purchase',
      [CONVERSION_EVENT_TYPES.SUBSCRIBE_SUCCESS]: 'Purchase',
    }

    const redditEventName = redditEventMap[eventData.event_type] || null
    const isCustomEvent = !redditEventName

    // 构建用户数据 - 至少需要一个匹配信号
    const userData: any = {}
    let hasMatchingSignal = false

    // 1. Reddit点击ID - 最高优先级
    if (eventData.reddit_click_id) {
      userData.click_id = eventData.reddit_click_id
      hasMatchingSignal = true
    }

    // 2. 邮箱地址 - 第二优先级
    if (eventData.user_email) {
      const hashedEmail = await hashEmail(eventData.user_email)
      if (hashedEmail) {
        userData.email = hashedEmail
        hasMatchingSignal = true
      }
    }

    // 3. Reddit UUID - 从cookie获取的重要信号
    if (eventData.reddit_uuid) {
      userData.uuid = eventData.reddit_uuid
      hasMatchingSignal = true
    }

    // 4. 外部ID (用户ID) - 哈希后使用
    if (eventData.user_id) {
      const hashedExternalId = await hashString(eventData.user_id)
      if (hashedExternalId) {
        userData.external_id = hashedExternalId
        hasMatchingSignal = true
      }
    }

    // 5. IP地址和用户代理
    if (eventData.ip_address) {
      userData.ip_address = eventData.ip_address
      hasMatchingSignal = true
    }
    if (eventData.user_agent) {
      userData.user_agent = eventData.user_agent
    }

    // 6. 屏幕分辨率
    if (eventData.screen_width && eventData.screen_height) {
      userData.screen_dimensions = {
        width: eventData.screen_width,
        height: eventData.screen_height,
      }
    }

    // 检查是否有足够的匹配信号
    if (!hasMatchingSignal) {
      console.warn('Reddit Conversions API: No valid matching signals found.', {
        event_type: eventData.event_type,
        has_email: !!eventData.user_email,
        has_user_id: !!eventData.user_id,
        has_ip: !!eventData.ip_address,
        has_click_id: !!eventData.reddit_click_id,
        has_uuid: !!eventData.reddit_uuid,
      })
      return
    }

    // 构建事件元数据
    const eventMetadata: any = {}

    // 生成转化ID用于去重
    const conversionId =
      eventData.conversion_id || (await generateConversionId(eventData))
    eventMetadata.conversion_id = conversionId

    if (eventData.value && eventData.value > 0) {
      eventMetadata.value_decimal = eventData.value
      eventMetadata.value = Math.round(eventData.value * 100) // 以分为单位的整数值
    }
    if (eventData.currency) {
      eventMetadata.currency = eventData.currency.toUpperCase()
    }
    if (eventData.transaction_id) {
      eventMetadata.products = [
        {
          id: eventData.transaction_id,
          name: eventData.product_name || 'Purchase',
          category: 'General',
        },
      ]
    }

    // 构建事件 - 根据Reddit API文档的正确结构
    const event: any = {
      event_at: new Date().toISOString(), // ISO 8601格式
      user: userData,
    }

    // 根据文档示例，事件类型结构
    if (isCustomEvent) {
      // 自定义事件
      event.event_type = {
        tracking_type: 'Custom',
        custom_event_name: eventData.event_type,
      }
    } else {
      // 标准事件 - 根据文档示例，tracking_type直接使用事件名称
      event.event_type = {
        tracking_type: redditEventName,
      }
    }

    // 添加事件元数据
    if (Object.keys(eventMetadata).length > 0) {
      event.event_metadata = eventMetadata
    }

    // 构建请求载荷
    const payload: any = {
      events: [event],
    }

    // 在非生产环境，启用测试模式
    if (!IS_PROD) {
      payload.test_mode = true
    }

    // 打印调试信息
    if (!IS_PROD) {
      console.log('Reddit API payload:', JSON.stringify(payload, null, 2))
    }

    const response = await fetch(
      `https://ads-api.reddit.com/api/v2.0/conversions/events/${pixelId}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${conversionToken}`,
          'Content-Type': 'application/json',
          'User-Agent': 'HeyBoss/1.0.0 (by /u/heyboss)',
        },
        body: JSON.stringify(payload),
      },
    )

    if (!response.ok) {
      const errorText = await response.text()
      console.error(
        'Failed to send event to Reddit Conversions API:',
        response.status,
        response.statusText,
        errorText,
      )
    } else {
      const responseData = await response.json()
      const logPrefix = IS_PROD
        ? 'Successfully sent event'
        : 'Successfully sent TEST event'
      console.log(
        `${logPrefix} to Reddit Conversions API:`,
        isCustomEvent ? eventData.event_type : redditEventName,
        'Matching signals:',
        Object.keys(userData),
        responseData,
      )
    }
  } catch (error) {
    console.error('Error sending event to Reddit Conversions API:', error)
  }
}
